import React from 'react';
import { Receipt, ArrowRight, FileText, ShieldCheck, Zap, Clock, CheckCircle, CreditCard, LifeBuoy, MessageSquare, FileQuestion, PhoneCall, Mail, Laptop, Smartphone, Database, Shield, Printer, BarChart, Brain, LayoutDashboard } from 'lucide-react';
import { motion } from 'framer-motion';
import { ProductLayout } from '../../components/layouts/ProductLayout';
import { AnimatedSection } from '../../components/AnimatedSection';
import { Wave } from '../../components/Wave';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';
import { getPageSEO } from '../../data/seoData';

export const Facelec = () => {
  // SEO Configuration
  useSEO(getPageSEO('facelec'));

  const productConfig = {
    name: 'Facelec+',
    color: 'orange',
    gradientFrom: 'from-orange-600',
    gradientTo: 'to-orange-700',
    description: 'Solución completa para facturación electrónica diseñada para empresas, profesionales independientes, PYMES e ingenieros. Automatiza, simplifica y garantiza el cumplimiento normativo de todos tus documentos fiscales',
    links: [{
      label: 'Inicio',
      path: '/facelec'
    }, {
      label: 'Características',
      path: '/facelec#caracteristicas'
    }, {
      label: 'Precios',
      path: '/facelec#precios'
    }, {
      label: 'Soporte',
      path: '/facelec#soporte'
    }],
    externalLink: {
      label: 'Acceder',
      url: 'https://facelec.ipse.com.ec/'
    }
  };
  const features = [{
    icon: <FileText className="w-8 h-8 text-orange-600" />,
    title: 'Documentos Electrónicos',
    description: 'Genera facturas, notas de crédito, notas de débito y guías de remisión autorizados por el SRI'
  }, {
    icon: <Database className="w-8 h-8 text-orange-600" />,
    title: 'Gestión Financiera',
    description: 'Gestiona ingresos y gastos con multicuenta. Recibe automáticamente documentos electrónicos del SRI'
  }, {
    icon: <BarChart className="w-8 h-8 text-orange-600" />,
    title: 'Proyecciones Financieras',
    description: 'Visualiza proyecciones de ingresos vs gastos con resumen financiero y gráficos ilustrativos'
  }, {
    icon: <Zap className="w-8 h-8 text-orange-600" />,
    title: 'Facturación Rápida',
    description: 'Utiliza atajos para realizar facturas rápidamente y genera proformas (cotizaciones) de manera eficiente'
  }, {
    icon: <MessageSquare className="w-8 h-8 text-orange-600" />,
    title: 'Soporte Integrado',
    description: 'Chat en vivo integrado directamente en la aplicación con notificaciones push para mantenerte informado'
  }, {
    icon: <Shield className="w-8 h-8 text-orange-600" />,
    title: 'Respaldo SRI',
    description: 'Todos los comprobantes electrónicos están autorizados por el SRI, garantizando total cumplimiento normativo'
  }];
  const pricingPlans = [{
    name: 'Plan 1',
    price: '$4.99',
    transactions: '15',
    description: 'Plan básico anual para comenzar'
  }, {
    name: 'Plan 2',
    price: '$19.99',
    transactions: '75',
    description: 'Ideal para pequeñas empresas'
  }, {
    name: 'Plan 3',
    price: '$29.99',
    transactions: '150',
    description: 'Para empresas en crecimiento'
  }, {
    name: 'Plan 4',
    price: '$49.99',
    transactions: '500',
    description: 'Solución empresarial completa',
    highlighted: true
  }, {
    name: 'Plan 5',
    price: '$79.99',
    transactions: '1000',
    description: 'Para grandes empresas'
  }, {
    name: 'Plan 6',
    price: '$99.99',
    transactions: 'Ilimitadas',
    description: 'Plan premium anual'
  }];
  const supportOptions = [{
    icon: <MessageSquare className="w-10 h-10 text-orange-600" />,
    title: 'Chat en vivo',
    description: 'Asistencia inmediata para resolver tus dudas sobre facturación electrónica en tiempo real.'
  }, {
    icon: <FileQuestion className="w-10 h-10 text-orange-600" />,
    title: 'Centro de ayuda',
    description: 'Biblioteca completa de tutoriales, guías y preguntas frecuentes sobre el uso de Facelec.'
  }, {
    icon: <PhoneCall className="w-10 h-10 text-orange-600" />,
    title: 'Soporte telefónico',
    description: 'Línea directa con especialistas en facturación electrónica para resolver consultas complejas.'
  }, {
    icon: <BarChart className="w-10 h-10 text-orange-600" />,
    title: 'Consultoría fiscal',
    description: 'Asesoramiento profesional sobre normativas fiscales y optimización de procesos de facturación.'
  }];
  const benefitsList = ['Generación proyecciones de presupuesto', 'Validación en tiempo real con las autoridades fiscales', 'Almacenamiento seguro en la nube con respaldos automáticos', 'Integración nativa con sistemas contables y ERPs externos', 'Notificaciones automáticas de gastos recibidos en el Portal SRI'];
  return <ProductLayout product={productConfig}>
      <section className="bg-gradient-to-b from-orange-800 via-orange-700 to-orange-600">
        <Wave color="#f9fafb" className="bg-orange-600" pattern="pattern1" />
      </section>

      <section id="caracteristicas" className="py-20 bg-gray-50">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">
                Características Principales
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Facelec ofrece un conjunto completo de herramientas diseñadas
                para simplificar tu proceso de facturación electrónica, ya seas
                un profesional independiente, una PYME o una gran empresa,
                garantizando el cumplimiento normativo y mejorando la eficiencia
                de tu actividad económica.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div className="col-span-full lg:col-span-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 text-white shadow-xl" whileHover={{
              scale: 1.02
            }}>
                <div className="flex items-center mb-6">
                  <div className="relative">
                    <Brain className="w-8 h-8 mr-3" />
                    <motion.div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full" animate={{
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.8, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity
                  }} />
                  </div>
                  <h3 className="text-2xl font-bold flex items-center">
                    💡 IPSE IA - Asistente Inteligente
                  </h3>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors">
                    <p className="font-medium">Análisis Predictivo</p>
                    <p className="text-sm opacity-90">
                      IPSE IA anticipa tendencias de ventas y comportamientos de
                      mercado con precisión
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors">
                    <p className="font-medium">Detección Proactiva</p>
                    <p className="text-sm opacity-90">
                      IPSE IA identifica automáticamente errores y patrones
                      inusuales en tiempo real
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors">
                    <p className="font-medium">Asistencia Continua</p>
                    <p className="text-sm opacity-90">
                      IPSE IA brinda soporte inteligente 24/7 para consultas
                      operativas y técnicas
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div className="col-span-full lg:col-span-3 bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-8 text-white shadow-xl mt-8" whileHover={{
              scale: 1.02
            }}>
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <LayoutDashboard className="w-8 h-8 mr-3" />
                  Dashboard Ejecutivo
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="font-medium">Gráficos Dinámicos</p>
                    <p className="text-sm opacity-90">
                      Visualización en tiempo real de ventas e ingresos vs
                      egresos
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="font-medium">Top Productos o Servicios</p>
                    <p className="text-sm opacity-90">
                      Análisis de los mejores productos o servicios que vendes y
                      sus montos
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="font-medium">Top Clientes</p>
                    <p className="text-sm opacity-90">
                      Análisis de los 10 mejores clientes y sus montos
                    </p>
                  </div>
                </div>
              </motion.div>
              {features.map((feature, index) => <motion.div key={index} className="bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow" whileHover={{
              y: -5
            }}>
                  <div className="mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-gray-600">{feature.description}</p>
                </motion.div>)}
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="white" className="bg-gray-50" pattern="pattern2" />
      <AnimatedSection className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Facturación Electrónica Simplificada
              </h2>
              <p className="text-gray-600 mb-8">
                Con Facelec, la facturación electrónica nunca ha sido tan
                sencilla. Ya seas ingeniero, consultor, empresa o emprendedor,
                nuestro sistema automatizado maneja todo el proceso, desde la
                generación hasta el almacenamiento seguro de tus documentos,
                permitiéndote enfocarte en hacer crecer tu negocio.
              </p>
              <ul className="space-y-4">
                {benefitsList.map((item, index) => <li key={index} className="flex items-center">
                    <Receipt className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Dashboard Facelec" className="rounded-lg shadow-2xl" />
              <div className="absolute inset-0 bg-gradient-to-tr from-orange-600/20 to-transparent rounded-lg" />
            </div>
          </div>
        </div>
      </AnimatedSection>

      <Wave color="#f9fafb" className="bg-white" pattern="pattern3" />
      <section id="precios" className="py-20 bg-gray-50">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Planes y Precios</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Todos nuestros planes incluyen acceso completo a todas las
                funcionalidades. Diseñados para adaptarse a las necesidades de
                profesionales independientes, PYMES y grandes empresas, elige el
                plan que mejor se ajuste a tu volumen de facturación.
              </p>
            </div>
            {/* Common Features Section */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-16">
              <h3 className="text-xl font-bold mb-6 text-center">
                Todas las características incluidas en cada plan:
              </h3>
              <div className="grid md:grid-cols-3 gap-8">
                <div>
                  <h4 className="font-semibold text-orange-600 mb-4">
                    Documentos Electrónicos
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Facturas autorizadas por el SRI</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Notas de Crédito y Débito</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Guías de Remisión</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Proformas y Cotizaciones</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-600 mb-4">
                    Gestión Financiera
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Gestión de Ingresos y Gastos</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Proyecciones Financieras</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Gráficos Ilustrativos</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Sistema Multicuenta</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-orange-600 mb-4">
                    Herramientas Avanzadas
                  </h4>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Recepción Automática SRI</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Atajos de Facturación Rápida</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Notificaciones Push</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Chat de Soporte Integrado</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            {/* Pricing Plans Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[{
              name: 'Plan 1',
              price: '$4.99',
              transactions: '15',
              description: 'Plan básico anual para comenzar'
            }, {
              name: 'Plan 2',
              price: '$19.99',
              transactions: '75',
              description: 'Ideal para pequeñas empresas'
            }, {
              name: 'Plan 3',
              price: '$29.99',
              transactions: '150',
              description: 'Para empresas en crecimiento'
            }, {
              name: 'Plan 4',
              price: '$49.99',
              transactions: '500',
              description: 'Solución empresarial completa',
              highlighted: true
            }, {
              name: 'Plan 5',
              price: '$79.99',
              transactions: '1000',
              description: 'Para grandes empresas'
            }, {
              name: 'Plan 6',
              price: '$99.99',
              transactions: 'Ilimitadas',
              description: 'Plan premium anual'
            }].map((plan, index) => <motion.div key={index} className={`relative rounded-2xl overflow-hidden border border-gray-200 shadow-lg'}`} whileHover={{
              y: -5,
              transition: {
                duration: 0.3
              }
            }}>
                  <div className={`p-8 bg-white ${plan.highlighted ? 'pt-10' : ''}`}>
                    <h3 className="text-xl font-bold mb-2">{plan.name}</h3>
                    <p className="text-gray-600 mb-4">{plan.description}</p>
                    <div className="mb-6">
                      <div className="text-5xl font-bold text-orange-600 mb-2">
                        {plan.transactions}
                        <span className="text-2xl font-normal text-orange-500">
                          {' '}
                          transacciones
                        </span>
                      </div>
                      <div className="text-lg font-medium text-gray-600">
                        {plan.price}
                        <span className="text-gray-500 text-base">
                          {' '}
                          por año
                        </span>
                      </div>
                    </div>
                    <motion.a href="https://facelec.ipse.com.ec/" target="_blank" rel="noopener noreferrer" whileHover={{
                  scale: 1.03
                }} whileTap={{
                  scale: 0.98
                }} className={`block w-full py-3 px-4 rounded-xl font-semibold transition-colors text-center ${plan.highlighted ? 'bg-orange-600 text-white hover:bg-orange-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                      Registrarse
                    </motion.a>
                  </div>
                </motion.div>)}
            </div>
            {/* Contact Section */}
            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">
                ¿Necesitas un plan personalizado para alto volumen de facturas?
              </p>
              <Link to="/contact">
                <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me gustaría obtener más información sobre Facelec+ y sus planes personalizados para facturación electrónica. ¿Podrían ayudarme a encontrar la mejor solución?" target="_blank" rel="noopener noreferrer" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="inline-flex items-center px-6 py-3 border border-orange-600 text-orange-600 rounded-xl hover:bg-orange-50 transition-colors font-medium">
                  Contacta con nuestro equipo de ventas
                  <ArrowRight className="ml-2 h-5 w-5" />
                </motion.a>
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="white" className="bg-gray-50" pattern="pattern1" />
      <section id="soporte" className="py-20 bg-white">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Soporte Especializado</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Nuestro equipo de expertos en facturación electrónica está
                disponible para ayudarte a resolver cualquier duda o problema
                técnico que puedas tener con Facelec.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {supportOptions.map((option, index) => <motion.div key={index} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center" whileHover={{
              y: -5
            }}>
                  <div className="flex justify-center mb-4">{option.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">{option.title}</h3>
                  <p className="text-gray-600">{option.description}</p>
                </motion.div>)}
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4">Academia Facelec</h3>
                  <p className="text-gray-600 mb-6">
                    Domina la facturación electrónica con nuestros cursos de
                    capacitación diseñados para todos los niveles, desde
                    principiantes hasta usuarios avanzados.
                  </p>
                  <ul className="space-y-3 mb-8">
                    {['Webinars mensuales sobre normativas fiscales', 'Tutoriales en video para todas las funciones', 'Guías paso a paso para casos específicos', 'Sesiones de capacitación personalizadas'].map((item, i) => <li key={i} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-orange-600 mr-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                  <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me interesa conocer sobre la academia Facelec, me gustaría obtener más información sobre el tema" target="_blank" rel="noopener noreferrer" whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} className="inline-flex items-center px-6 py-3 bg-orange-600 text-white rounded-xl hover:bg-orange-700 transition-colors font-medium">
                    Conoce más de la academia
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </motion.a>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Capacitación en línea" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Soporte técnico" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Capacitación grupal" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1573164713988-8665fc963095?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Webinar fiscal" className="w-full h-48 object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="#f9fafb" className="bg-white" pattern="pattern2" />
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">
            ¿Listo para modernizar tu facturación?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Únete a miles de profesionales, empresas y PYMES que ya optimizaron
            su proceso de facturación con Facelec. Perfecta para ingenieros,
            consultores, comercios y todo tipo de actividad profesional.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4 max-w-4xl mx-auto">
            <motion.a href="https://facelec.ipse.com.ec/" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="w-full md:flex-1 bg-white border border-orange-600 text-orange-600 px-6 py-4 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center">
              <Laptop className="w-5 h-5 mr-2" />
              Ingresar a Facelec Web
            </motion.a>
            <motion.a href="https://play.google.com/store/apps/details?id=com.ipse.quickcont.facelec" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="w-full md:flex-1 bg-white border border-orange-600 text-orange-600 px-6 py-4 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/d/d7/Android_robot.svg/872px-Android_robot.svg.png" alt="Android Logo" className="w-5 h-5 mr-2" />
              Descargar para Android
            </motion.a>
            <motion.a href="https://apps.apple.com/ec/app/facelec/id6503172453" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="w-full md:flex-1 bg-white border border-orange-600 text-orange-600 px-6 py-4 rounded-xl font-semibold hover:bg-orange-50 transition-colors flex items-center justify-center">
              <img src="https://upload.wikimedia.org/wikipedia/commons/thumb/f/fa/Apple_logo_black.svg/814px-Apple_logo_black.svg.png" alt="Apple Logo" className="w-5 h-5 mr-2" />
              Descargar para iOS
            </motion.a>
          </div>
        </div>
      </AnimatedSection>
    </ProductLayout>;
};